import type { GameConfiguration } from '../types';
import { getActiveModuleIds, getActiveVariantId } from '../configuration';

export type VinhosScoringModuleId =
  | 'base'
  | 'experts'
  | 'islands'
  | 'tasting-room'
  | 'connoisseur';

export type VinhosScoringRecord = {
  id: string;
  gameId: 'vinhos-deluxe';
  moduleId: VinhosScoringModuleId;
  category: 'end-game' | 'mid-game' | 'variant';
  element: string;
  timing: 'end-game' | 'mid-game' | 'both';
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: 'verified' | 'component-dependent' | 'partial';
  verified: boolean;
  order: number;
  keywords: string[];
};

export const vinhosScoringRecords: VinhosScoringRecord[] = [
  {
    id: 'vinhos-export-vp',
    gameId: 'vinhos-deluxe',
    moduleId: 'base',
    category: 'mid-game',
    element: 'Eksport (VP natychmiast)',
    timing: 'mid-game',
    scoring:
      'Punkty zdobyte natychmiast podczas eksportu — zgodnie z nadrukiem / efektem eksportu.',
    conditions: 'Legalny eksport w trakcie partii.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 10,
    keywords: ['eksport'],
  },
  {
    id: 'vinhos-fairs',
    gameId: 'vinhos-deluxe',
    moduleId: 'base',
    category: 'mid-game',
    element: 'Trzy targi',
    timing: 'mid-game',
    scoring:
      'Punkty z trzech targów — według rankingu / nadruku rozstrzygnięcia targu.',
    conditions: 'Po rozstrzygnięciu każdego targu.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 20,
    keywords: ['targi', 'fair'],
  },
  {
    id: 'vinhos-barrels-export-area',
    gameId: 'vinhos-deluxe',
    moduleId: 'base',
    category: 'end-game',
    element: 'Beczki / pozycje eksportu',
    timing: 'end-game',
    scoring:
      'Punkty z beczek i pozycji w obszarze eksportu — odczyt z planszy / nadruku.',
    conditions: 'Punktacja końcowa.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 30,
    keywords: ['beczki', 'eksport'],
  },
  {
    id: 'vinhos-end-tiles',
    gameId: 'vinhos-deluxe',
    moduleId: 'base',
    category: 'end-game',
    element: 'Kafle punktacji / magnaci',
    timing: 'end-game',
    scoring:
      'Punkty z kafli punktacji, magnatów lub aktywnych modułów — zgodnie z nadrukiem.',
    conditions: 'Przy rozliczeniu końcowym.',
    exceptions: 'Nie punktuj automatycznie pozostałych win bez efektu końcowego.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 40,
    keywords: ['magnat', 'kafel'],
  },
  {
    id: 'vinhos-money-convert',
    gameId: 'vinhos-deluxe',
    moduleId: 'base',
    category: 'end-game',
    element: 'Przeliczenie pieniędzy / pozostałości',
    timing: 'end-game',
    scoring:
      'Ewentualne przeliczenie pieniędzy i innych pozostałości zgodnie z planszą (nadruk).',
    conditions: 'Tabela końcowa na planszy.',
    exceptions: 'Bagos ≠ VP poza tabelą końcową.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 50,
    keywords: ['pieniądze', 'bagos', 'tabela'],
  },
  {
    id: 'vinhos-experts',
    gameId: 'vinhos-deluxe',
    moduleId: 'experts',
    category: 'end-game',
    element: 'Eksperci',
    timing: 'both',
    scoring:
      'Efekty i VP ekspertów — zgodnie z nadrukiem kafli ekspertów (PARTIAL katalog).',
    conditions: 'Moduł Experts włączony i kompatybilny z wariantem.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 100,
    keywords: ['eksperci'],
  },
  {
    id: 'vinhos-islands',
    gameId: 'vinhos-deluxe',
    moduleId: 'islands',
    category: 'end-game',
    element: 'Wyspy',
    timing: 'both',
    scoring: 'Punkty / efekty wysp — zgodnie z nadrukiem (PARTIAL).',
    conditions: 'Moduł Islands włączony.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 110,
    keywords: ['wyspy'],
  },
  {
    id: 'vinhos-tasting',
    gameId: 'vinhos-deluxe',
    moduleId: 'tasting-room',
    category: 'end-game',
    element: 'Tasting Room',
    timing: 'both',
    scoring: 'Efekty degustacji — zgodnie z nadrukiem modułu.',
    conditions: 'Moduł Tasting Room włączony.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 120,
    keywords: ['tasting', 'degustacja'],
  },
  {
    id: 'vinhos-connoisseur',
    gameId: 'vinhos-deluxe',
    moduleId: 'connoisseur',
    category: 'end-game',
    element: 'Connoisseur / podwójne kafle',
    timing: 'end-game',
    scoring:
      'Podwójny kafel liczy się jako jeden kafel przy punktowaniu (zajmuje dwa miejsca). Inne VP — nadruk.',
    conditions: 'Moduł Connoisseur włączony.',
    exceptions: 'Nie licz podwójnego kafla jako dwóch przy scoringu.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 130,
    keywords: ['connoisseur', 'podwójny'],
  },
];

function mapConfigModules(config: GameConfiguration): VinhosScoringModuleId[] {
  const ids: VinhosScoringModuleId[] = ['base'];
  const active = getActiveModuleIds(config);
  for (const id of active) {
    if (
      id === 'experts' ||
      id === 'islands' ||
      id === 'tasting-room' ||
      id === 'connoisseur'
    ) {
      ids.push(id);
    }
  }
  return ids;
}

export function filterVinhosScoring(
  config: GameConfiguration,
  options: { showAll?: boolean } = {}
): VinhosScoringRecord[] {
  if (options.showAll) {
    return vinhosScoringRecords.slice().sort((a, b) => a.order - b.order);
  }
  const enabled = new Set(mapConfigModules(config));
  // Touch variant so callers keep config-aware filtering honest.
  void getActiveVariantId(config);
  return vinhosScoringRecords
    .filter((r) => enabled.has(r.moduleId))
    .sort((a, b) => a.order - b.order);
}

export function validateVinhosScoringTables(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of vinhosScoringRecords) {
    if (ids.has(r.id)) issues.push(`Duplicate vinhos scoring id ${r.id}`);
    ids.add(r.id);
  }
  const baseConfig: GameConfiguration = {
    schemaVersion: 2,
    playerCount: 2,
    activeVariantId: 'special-vintage-2016',
    specialVintage2016: true,
    vintage2010: false,
    experts: false,
    islands: false,
    tastingRoom: false,
    connoisseur: false,
  };
  const base = filterVinhosScoring(baseConfig);
  if (!base.every((r) => r.moduleId === 'base')) {
    issues.push('Base Vinhos filter leaked expansion rows');
  }
  if (!base.some((r) => r.id === 'vinhos-export-vp')) {
    issues.push('Missing export scoring');
  }
  const withConnoisseur: GameConfiguration = {
    ...baseConfig,
    connoisseur: true,
  };
  const expanded = filterVinhosScoring(withConnoisseur);
  if (!expanded.some((r) => r.id === 'vinhos-connoisseur')) {
    issues.push('Connoisseur scoring missing when enabled');
  }
  return issues;
}
